// [S-277] Precedent visibility — the tier badge + the Exclude-from-shared /
// Re-share control. Surfaced on the precedent browse card and the detail view.
//
// Who can change it (mirrors canChangePrecedentVisibility, enforced server-side):
// the deal's OWNER (its uploader) or a tenant_admin. Everyone sees the badge; only
// those two see the button. Forward-only — excluding hides the deal from every
// other member's search/browse/agent immediately, but never touches audit or
// already-derived drafts.
import { useState } from "react";
import { useSession } from "../auth/AuthProvider";
import { sessionCan } from "../auth/capabilities";
import { Button, Dialog, DropdownMenu, DropdownMenuItem } from "../components";
import { getApiErrorMessage } from "../data/api";
import {
  useSetPrecedentVisibility,
  type PrecedentVisibility,
} from "../data/precedentsQuery";

const TIER_LABEL: Record<PrecedentVisibility, string> = {
  tenant: "Studio-shared",
  business_affairs: "Business affairs",
  private: "Private",
};

/** The tier badge. Rendered for the non-default tiers only (a `tenant`-shared
 *  precedent is the norm and needs no chip); pass `always` to force it. */
export function PrecedentVisibilityBadge({
  visibility,
  always = false,
}: {
  visibility: PrecedentVisibility;
  always?: boolean;
}): JSX.Element | null {
  // Only the non-default tiers get a chip (a `tenant`-shared precedent is the
  // norm). `always` forces it; anything unrecognized renders nothing.
  if (!always && visibility !== "private" && visibility !== "business_affairs") {
    return null;
  }
  return (
    <span
      className={`prec__vis-badge prec__vis-badge--${visibility}`}
      data-testid="prec-vis-badge"
      title={
        visibility === "private"
          ? "Excluded from the shared corpus — visible to you + admins only"
          : visibility === "business_affairs"
            ? "Business-affairs tier — visible to BA + admins"
            : "Shared with everyone in your studio"
      }
    >
      {TIER_LABEL[visibility]}
    </span>
  );
}

/** The exclude / re-share button + confirm dialog. Renders null for callers who
 *  can't manage this precedent (non-owner, non-admin). */
export function PrecedentVisibilityControl({
  documentId,
  visibility,
  ownerUid,
  tenantId,
}: {
  documentId: string;
  visibility: PrecedentVisibility;
  ownerUid: string;
  tenantId: string | undefined;
}): JSX.Element | null {
  const session = useSession();
  const setVis = useSetPrecedentVisibility();
  const [confirmingExclude, setConfirmingExclude] = useState(false);

  const canManage =
    session != null &&
    (session.uid === ownerUid || sessionCan(session, "exclusion:manage"));
  if (!canManage) return null;

  const change = (target: PrecedentVisibility): void => {
    setVis.mutate({
      id: documentId,
      visibility: target,
      ...(tenantId !== undefined ? { tenantId } : {}),
    });
  };
  const closeConfirm = (): void => {
    setVis.reset();
    setConfirmingExclude(false);
  };

  // [S-275] Three tiers now — the menu offers every transition except the
  // current one. Excluding (→ private) hides the deal from everyone, so it
  // routes through a confirm; the reversible transitions (studio-shared /
  // business affairs) apply directly.
  return (
    <div className="prec__vis">
      <DropdownMenu
        trigger={
          <Button
            type="button"
            variant="bare"
            className="prec__vis__btn"
            disabled={setVis.isPending}
            data-testid="prec-vis-open"
            aria-label="Change this precedent's visibility"
          >
            {setVis.isPending ? "Saving…" : `Visibility: ${TIER_LABEL[visibility]} ▾`}
          </Button>
        }
      >
        {visibility !== "tenant" && (
          <DropdownMenuItem onSelect={() => change("tenant")}>
            Re-share to studio
          </DropdownMenuItem>
        )}
        {visibility !== "business_affairs" && (
          <DropdownMenuItem onSelect={() => change("business_affairs")}>
            Mark business affairs
          </DropdownMenuItem>
        )}
        {visibility !== "private" && (
          <DropdownMenuItem onSelect={() => setConfirmingExclude(true)}>
            Exclude from shared
          </DropdownMenuItem>
        )}
      </DropdownMenu>

      <Dialog
        open={confirmingExclude}
        onOpenChange={(isOpen) => {
          if (!isOpen) closeConfirm();
        }}
        title="Exclude from the shared corpus?"
        description="It disappears from every other member's search, browse, and agent immediately. You and your studio admins keep access. Audit and any existing drafts are untouched."
      >
        {setVis.isError && (
          <div className="prec__confirm__error" role="alert" data-testid="prec-vis-error">
            {getApiErrorMessage(setVis.error) ?? "Couldn't change visibility"}
          </div>
        )}
        <div className="prec__confirm__ft">
          <Button
            variant="ghost"
            type="button"
            disabled={setVis.isPending}
            onClick={closeConfirm}
            data-testid="prec-vis-cancel"
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            type="button"
            disabled={setVis.isPending}
            data-testid="prec-vis-confirm"
            onClick={() =>
              setVis.mutate(
                {
                  id: documentId,
                  visibility: "private",
                  ...(tenantId !== undefined ? { tenantId } : {}),
                },
                { onSuccess: () => setConfirmingExclude(false) }
              )
            }
          >
            {setVis.isPending ? "Saving…" : "Exclude"}
          </Button>
        </div>
      </Dialog>
    </div>
  );
}
